// src/components/features/timetables/TimetablesTab/ResultsPage.tsx
import { PagePadding, ResultsPage } from '../../../widget/Pages/Pages';
import { TimetableSearchResultList } from '../SearchResultList/TimetableSearchResultList';
import type { TimetableLineItem } from '../../../../data/useTimetableSearch';
import { TimetableSearchForm } from './SearchForm';
import { StateMessage } from '../../../base/StateMessage/StateMessage';
import { StateWrapper } from '../../../base/StateWrapper/StateWrapper';
import { EmptyState } from '../../../base/EmptyState/EmptyState';
// styles
import styles from './TimetableResultsPage.module.css';

interface TimetableResultsPageProps {
  draftQuery: string;
  submittedQuery: string | null;
  onQueryChange: (value: string) => void;
  onSearch: (event: Event) => void;
  submitError: string | null;
  hasSubmitted: boolean;
  loading: boolean;
  error: Error | null;
  lines: TimetableLineItem[];
  onShowDetails: (detailsId: string) => void;
}

export function TimetableResultsPage({
  draftQuery,
  submittedQuery,
  onQueryChange,
  onSearch,
  submitError,
  hasSubmitted,
  loading,
  error,
  lines,
  onShowDetails,
}: TimetableResultsPageProps) {
  const showResultsSummary =
    hasSubmitted && !loading && !error && submittedQuery && lines.length > 0;

  return (
    <ResultsPage padding="flush">
      <PagePadding className={styles.summarySection}>
        <TimetableSearchForm
          query={draftQuery}
          onQueryChange={onQueryChange}
          onSearch={onSearch}
          submitError={submitError}
          loading={loading}
          hasSubmitted={hasSubmitted}
        />
      </PagePadding>

      {showResultsSummary && (
        <p className={styles.resultsSummary}>
          Showing <strong>{lines.length}</strong> results for{' '}
          <strong>&quot;{submittedQuery}&quot;</strong>
        </p>
      )}

      <StateWrapper
        Wrapper={({ children }) => (
          <PagePadding className={styles.stateExtraTopPadding}>
            {children}
          </PagePadding>
        )}
        loading={loading}
        error={error}
        isEmpty={!loading && !error && lines.length === 0}
        renderError={(lookupError) => (
          <StateMessage
            state="error"
            title="Timetable lookup failed"
            message={lookupError.message}
          />
        )}
        renderEmpty={() => (
          <EmptyState
            title={`No results found for "${submittedQuery ?? 'your search term'}"`}
            message="Double-check for any spelling mistakes or try a different search term."
          />
        )}
      >
        {lines.length > 0 && (
          <TimetableSearchResultList
            aria-label="Timetable search results list"
            lines={lines}
            onSelectLine={(line) => onShowDetails(line.id)}
          />
        )}
      </StateWrapper>
    </ResultsPage>
  );
}
